import { useEffect, useState } from "react";
import { api } from "@/lib/api";

const DIRECTIONS = [
  { key: "seniors", label: "Seniors / Easy", hint: "Across and down only, forward" },
  { key: "classic", label: "Classic", hint: "Adds backwards words" },
  { key: "challenge", label: "Challenge", hint: "All 8 directions, incl. diagonals" },
];
const AUDIENCES = [["Children", "Kids"], ["Adult", "Adults"], ["Seniors", "Seniors"]];
const LEVEL = { seniors: 0, classic: 1, challenge: 2 };
const DIFF = ["easy", "medium", "hard"];

const lbl = "font-ui text-xs uppercase tracking-[0.2em] font-semibold text-slate-400/90 mb-2 block";
const chip = (on) => `rounded-lg py-2 px-2 font-ui text-sm font-medium transition-all ${on ? "bg-gold-400 text-stone-950" : "bg-navy-950/50 text-slate-300 border border-gold-500/12"}`;

export const printedLevel = (difficulty, directions) =>
  DIFF[Math.max(DIFF.indexOf(difficulty), LEVEL[directions] ?? 1)];

export const PuzzleSetup = ({ mode, difficulty, value, onChange }) => {
  const [trims, setTrims] = useState([]);
  useEffect(() => { api.meta().then((m) => setTrims((m.kdp_trims || []).filter((t) => t.group === "book"))).catch(() => {}); }, []);
  const ws = mode === "wordsearch";
  const set = (k, v) => onChange({ ...value, [k]: v });
  const setAudience = (a) => onChange({ ...value, audience: a, ...(ws && a === "Seniors" ? { directions: "seniors", largePrint: true } : {}) });

  return (
    <div className="space-y-4" data-testid="puzzle-setup">
      {ws && (
        <div>
          <label className={lbl}>Word directions</label>
          <div className="grid grid-cols-3 gap-2">
            {DIRECTIONS.map((d) => (
              <button key={d.key} data-testid={`ws-directions-${d.key}`} onClick={() => set("directions", d.key)} className={chip(value.directions === d.key)}>
                {d.label}
              </button>
            ))}
          </div>
          <p className="font-ui text-xs text-slate-500 mt-2" data-testid="ws-directions-hint">
            {DIRECTIONS.find((d) => d.key === value.directions)?.hint}. Printed as{" "}
            <span className="text-gold-300 capitalize" data-testid="ws-printed-level">{printedLevel(difficulty, value.directions)}</span>.
          </p>
        </div>
      )}
      <div>
        <label className={lbl}>Audience</label>
        <div className="grid grid-cols-3 gap-2">
          {AUDIENCES.map(([k, l]) => (
            <button key={k} data-testid={`puzzle-audience-${k.toLowerCase()}`} onClick={() => setAudience(k)} className={chip(value.audience === k)}>{l}</button>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className={lbl}>Trim size</label>
          <select data-testid="puzzle-trim-select" value={value.trim} onChange={(e) => set("trim", e.target.value)}
            className="bg-navy-950/60 border border-gold-500/12 rounded-lg px-3 py-2 font-ui text-sm text-slate-100 outline-none focus:border-gold-400 w-full">
            {(trims.length ? trims : [{ key: value.trim, label: value.trim }]).map((t) => <option key={t.key} value={t.key} className="bg-navy-900">{t.label}</option>)}
          </select>
        </div>
        {ws && (
          <label className="flex items-center gap-3 sm:mt-6 cursor-pointer" data-testid="large-print-label">
            <input type="checkbox" data-testid="large-print-toggle" checked={value.largePrint} onChange={(e) => set("largePrint", e.target.checked)} className="w-4 h-4 accent-amber-400" />
            <span className="font-ui text-sm text-slate-200">Large print <span className="text-slate-500">(bigger letters, grid max 13×13)</span></span>
          </label>
        )}
        {mode === "maze" && (
          <div>
            <label className={lbl}>Answer keys per page</label>
            <div className="grid grid-cols-2 gap-2">
              {[4, 2].map((n) => (
                <button key={n} data-testid={`answer-keys-per-page-${n}`} onClick={() => set("answerKeys", n)} className={chip((value.answerKeys || 4) === n)}>
                  {n === 4 ? "4 (2×2)" : "2 (larger)"}
                </button>
              ))}
            </div>
            <p className="font-ui text-xs text-slate-500 mt-2">Very detailed mazes automatically get more room.</p>
            <p className="font-ui text-xs text-slate-500 mt-1" data-testid="maze-theme-check-cost">
              Every illustration is checked by AI for theme match before it is accepted (about $0.0025 each, ~$0.13 for 50 pages, included). Flagged pages are never redrawn automatically.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};
